import { z } from 'zod'
import type { AITool } from './types'
import type { CategorizedOneRepMax } from '../../types'
import { getExercisePlateauDetails } from '../analysis'
import { formatWeightValue, getWeightUnit } from '../unit-utils'

// Parameters schema - simple, no includeNonPlateaus
const parametersSchema = z.object({})

export const getDetectedPlateaus: AITool = {
  name: 'getDetectedPlateaus',
  description: 'Detect exercise plateaus.',
  parameters: parametersSchema,
  
  execute: async (args, context) => {
    const { workoutData, userSettings } = context
    
    // Get unit system for display
    const unitSystem = userSettings?.settings?.units || 'metric'
    
    // Get plateau threshold from user settings
    const plateauThreshold = userSettings?.settings?.algorithms?.exercisePlateauThreshold || 2.0
    
    // Check if workout data is available
    if (!workoutData.oneRepMaxes || workoutData.oneRepMaxes.length === 0) {
      return {
        summary: 'No exercise data available for plateau detection.',
        plateauCount: 0,
        totalExercises: 0,
        plateauThreshold,
        plateaus: []
      }
    }
    
    // Detect plateaus for all exercises
    const plateaus: Array<{
      exerciseName: string
      exerciseTemplateId: string | null
      primaryMover: string
      plateauDuration: number
      trendChange: number
      currentValue: number
      unit: string
      dateRange: {
        start: string
        end: string
      }
    }> = []
    
    let totalExercises = 0
    
    // Process each exercise
    workoutData.oneRepMaxes.forEach((exercise: CategorizedOneRepMax) => {
      // Skip if not enough data
      if (!exercise.chartData || exercise.chartData.length < 2) return
      
      totalExercises++
      
      // Get plateau details using the same function as the dashboard
      const plateauDetails = getExercisePlateauDetails(exercise.chartData, plateauThreshold)
      
      if (!plateauDetails || !plateauDetails.isPlateauing) return
      
      // Find the exercise template ID from workouts
      let exerciseTemplateId: string | null = null
      workoutData.workouts?.find(w => 
        w.exercises.some(ex => {
          if (ex.title === exercise.name) {
            exerciseTemplateId = ex.exercise_template_id
            return true
          }
          return false
        })
      )
      
      // Calculate plateau duration
      const plateauDuration = Math.round(
        (plateauDetails.recentDate.getTime() - plateauDetails.baseline30DaysAgo.getTime()) / 
        (1000 * 60 * 60 * 24)
      )
      
      // Format current value based on exercise type
      const isBodyweight = exercise.isBodyweight || 
        (exercise.chartData.length > 0 && exercise.chartData[0].isBodyweight)
      
      let currentValue: number
      let unit: string
      
      if (isBodyweight) {
        currentValue = Math.round(plateauDetails.trendToday)
        unit = 'reps'
      } else {
        currentValue = plateauDetails.trendToday
        unit = getWeightUnit(unitSystem)
      }
      
      plateaus.push({
        exerciseName: exercise.name,
        exerciseTemplateId,
        primaryMover: exercise.primaryMover || 'Unknown',
        plateauDuration,
        trendChange: Math.round(plateauDetails.changePercentage * 10) / 10, // Round to 1 decimal
        currentValue: isBodyweight ? currentValue : parseFloat(formatWeightValue(currentValue, unitSystem)),
        unit,
        dateRange: {
          start: plateauDetails.baseline30DaysAgo.toISOString().split('T')[0],
          end: plateauDetails.recentDate.toISOString().split('T')[0]
        }
      })
    })
    
    // Sort plateaus by duration (longest first)
    plateaus.sort((a, b) => b.plateauDuration - a.plateauDuration)
    
    // Generate summary
    let summary: string
    if (plateaus.length === 0) {
      summary = `No plateaus detected across ${totalExercises} tracked exercises (using ${plateauThreshold}% threshold).`
    } else if (plateaus.length === 1) {
      const p = plateaus[0]
      summary = `Found 1 plateau: ${p.exerciseName} (${p.plateauDuration} days at ${p.currentValue}${p.unit}).`
    } else {
      const topPlateaus = plateaus.slice(0, 3).map(p => 
        `${p.exerciseName} (${p.plateauDuration} days)`
      ).join(', ')
      summary = `Found ${plateaus.length} plateaus: ${topPlateaus}${plateaus.length > 3 ? ', ...' : ''}.`
    }
    
    return {
      summary,
      plateauCount: plateaus.length,
      totalExercises,
      plateauThreshold,
      plateaus
    }
  }
}